class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.section = this.dataset.section;
    this.productFormId = this.dataset.productFormId || `product-form-${this.section}`;
    
    // Get the form using the explicit data attribute (most reliable)
    this.form = document.getElementById(this.productFormId);
    
    // Fallback: search for form if not found
    if (!this.form) {
      this.form = document.querySelector(`form[data-product-form="${this.section}"]`);
    }
    
    // Last resort fallback
    if (!this.form) {
      this.form = document.querySelector(`form[id*="product-form"]`);
    }

    // Log for debugging
    if (!this.form) {
      console.warn(
        `[StickyATC] Form not found. Looking for id="${this.productFormId}". ` +
        `Available forms: ${Array.from(document.querySelectorAll('form')).map(f => f.id).join(', ')}`
      );
    }

    this.variantSelects = document.querySelector(`#variant-selects-${this.section}`);
    this.trigger = this.querySelector('.sticky-atc__select');
    this.submitButtons = this.querySelectorAll('[data-sticky-atc-submit]');

    // Move modal to <body> so position:fixed isn't trapped by a transformed ancestor
    this.modal = this.querySelector('[data-sticky-atc-modal]');
    if (this.modal) {
      document.body.appendChild(this.modal);
    }

    this.bindEvents();
    this.observeHeroButton();
  }

  bindEvents() {
    // Open variant selector modal
    if (this.trigger) {
      this.trigger.addEventListener('click', () => this.openModal());
    }

    // Modal interactions (delegated for relocated modal)
    if (this.modal) {
      this.modal.addEventListener('click', (e) => {
        if (e.target.closest('[data-sticky-atc-close]')) {
          this.closeModal();
          return;
        }
        const optionBtn = e.target.closest('[data-sticky-atc-option]');
        if (optionBtn) {
          this.selectVariant(optionBtn);
        }
      });
    }

    // Close modal on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal?.classList.contains('is-open')) {
        this.closeModal();
      }
    });

    // Sync when variant selectors change (from main product section)
    if (this.variantSelects) {
      this.variantSelects.addEventListener('change', () => this.syncFromForm());
    }

    // Listen for custom variant:change event from other theme components
    document.addEventListener('variant:change', (e) => {
      this.syncFromVariant(e.detail?.variant);
    });

    // Add to cart: handled directly via fetch, NOT via native form submit.
    // The hero add-to-cart button in buy-buttons.liquid is now type="button"
    // (it does its own fetch), so the <product-form> wrapper no longer has
    // a submit button inside itself. If we let these buttons trigger a
    // native form submit (via type="submit" + form="..."), <product-form>'s
    // own submit handler still catches it and errors out looking for its
    // (now missing) internal submit button, before /cart/add.js is ever
    // called. Doing our own fetch here sidesteps that entirely.
    this.submitButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.addToCart(btn);
      });
    });
  }

  addToCart(button) {
    if (!this.form) {
      console.error('[StickyATC] Form not found. Cannot add to cart.');
      return;
    }

    const idInput = this.form.querySelector('input[name="id"]');
    const quantityInput = this.form.querySelector('input[name="quantity"]');

    if (!idInput || !idInput.value) {
      console.error('[StickyATC] No variant selected. Please select an option.');
      return;
    }

    const variantId = idInput.value;
    const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

    const originalHTML = button.innerHTML;
    this.submitButtons.forEach((b) => (b.disabled = true));

    // Open the drawer right away for responsiveness.
    const cartDrawer = document.querySelector('cart-drawer');
    const openDrawer = () => {
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    };

    this.getCartItemCount()
      .catch(() => null) // fine if this fails, just means we can't compare
      .then((countBeforeAdd) => {
        return fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [{ id: parseInt(variantId, 10), quantity: quantity }],
          }),
        })
          .then((response) => {
            if (!response.ok) {
              return response.json().then((err) => {
                throw err;
              });
            }
            return response.json();
          })
          .then(() => {
            openDrawer();
            // Shopify's cart state isn't always immediately consistent the
            // instant /cart/add.js resolves (the drawer's own upsell "add"
            // flow works around the same thing with a fixed delay before
            // its /cart.js refetch). Instead of guessing a delay, poll
            // briefly until the item count actually increases, then tell
            // the drawer to refresh — this is what was making the sticky
            // bar look like it "needs a refresh" to show the new item.
            return this.waitForCartToUpdate(countBeforeAdd);
          })
          .then(() => {
            document.dispatchEvent(new Event('cart:update'));
          });
      })
      .catch((error) => {
        console.error('[StickyATC] Error adding to cart:', error);
        alert(
          (error && (error.description || error.message)) ||
            'Error adding product to cart. Please try again.'
        );
      })
      .finally(() => {
        this.submitButtons.forEach((b) => (b.disabled = false));
        button.innerHTML = originalHTML;
      });
  }

  getCartItemCount() {
    return fetch(window.Shopify.routes.root + 'cart.js?ts=' + Date.now(), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
      },
      cache: 'no-store',
    })
      .then((res) => res.json())
      .then((cart) => cart.item_count);
  }

  // Polls /cart.js a few times until item_count changes (or we give up),
  // so the drawer refresh is based on real, settled cart state rather
  // than a fixed guessed delay.
  waitForCartToUpdate(countBeforeAdd, attempt = 0) {
    const maxAttempts = 5;
    const delay = 400;

    return new Promise((resolve) => {
      setTimeout(() => {
        this.getCartItemCount()
          .then((count) => {
            if (
              countBeforeAdd === null ||
              count !== countBeforeAdd ||
              attempt >= maxAttempts
            ) {
              resolve();
            } else {
              resolve(this.waitForCartToUpdate(countBeforeAdd, attempt + 1));
            }
          })
          .catch(() => resolve());
      }, delay);
    });
  }

  openModal() {
    if (!this.modal) return;
    this.modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  selectVariant(button) {
    if (!this.form) {
      console.error('[StickyATC] Form not found. Cannot select variant.');
      return;
    }

    const variantId = button.dataset.variantId;
    const input = this.form.querySelector('input[name="id"]');
    
    if (!input) {
      console.error('[StickyATC] Variant ID input not found in form.');
      return;
    }

    // Update the hidden variant ID input
    input.value = variantId;
    input.dispatchEvent(new Event('change', { bubbles: true }));

    // Mirror the click on the real variant inputs so price/inventory/UI update everywhere
    const matchingRadio = this.variantSelects?.querySelector(
      `input[value="${button.textContent.trim()}"]`
    );
    if (matchingRadio) {
      matchingRadio.checked = true;
      matchingRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Update modal UI
    this.modal.querySelectorAll('[data-sticky-atc-option]').forEach((b) =>
      b.classList.toggle('sticky-atc__option--selected', b === button)
    );

    // Update button label
    const label = this.querySelector('[data-sticky-atc-selected-label]');
    if (label) label.textContent = button.textContent.trim();

    this.closeModal();
  }

  syncFromForm() {
    if (!this.form || !this.modal) return;
    
    const idInput = this.form.querySelector('input[name="id"]');
    if (!idInput) return;

    const selectedOption = this.modal.querySelector(
      `[data-variant-id="${idInput.value}"]`
    );
    if (selectedOption) {
      const label = this.querySelector('[data-sticky-atc-selected-label]');
      if (label) label.textContent = selectedOption.textContent.trim();
      this.modal.querySelectorAll('[data-sticky-atc-option]').forEach((b) =>
        b.classList.toggle('sticky-atc__option--selected', b === selectedOption)
      );
    }
  }

  syncFromVariant(variant) {
    if (!variant) return;
    const label = this.querySelector('[data-sticky-atc-selected-label]');
    const submitBtn = this.querySelector('[data-sticky-atc-submit]');
    const btnText = this.querySelector('[data-sticky-atc-btn-text]');
    
    if (label) label.textContent = variant.title;
    if (submitBtn) submitBtn.disabled = !variant.available;
    if (btnText) {
      btnText.textContent = variant.available
        ? (window.stickyAtcStrings?.addToCart || 'Add to Cart')
        : (window.stickyAtcStrings?.soldOut || 'Sold Out');
    }
  }

  observeHeroButton() {
    // Show sticky bar once the main Add to Cart button scrolls out of view.
    // The hero button in buy-buttons.liquid is icon-only now and has no
    // name="add" attribute anymore, so fall back to its id/class.
    const heroAddToCart =
      document.querySelector(`#ProductInfo-${this.section} [name="add"]`) ||
      document.querySelector(`#AddToCartIcon-${this.section}`) ||
      document.querySelector(`#ProductInfo-${this.section} .button--cart-icon`);
    
    if (!heroAddToCart) {
      // If no hero button, always show sticky bar
      this.classList.add('is-visible');
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        this.classList.toggle('is-visible', !entry.isIntersecting);
      },
      { threshold: 0 }
    );
    observer.observe(heroAddToCart);
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart);